// ============================================================================
// USE WORK ITEM SEQUENCES HOOK
// Fetches the client's work_item_number_sequences rows. Callers use the
// per-type `config` blob (show_in_type_filter, custom_statuses, priorities,
// sla_config, …) to drive client-specific visibility and workflow behavior.
// ============================================================================

'use client';

import { useCallback, useEffect, useState } from 'react';
import { authService } from '@/lib/authService';
import type { WorkItemSequenceLike } from '@/lib/operations/optionalItemTypes';

export interface UseWorkItemSequencesResult {
  /** Raw sequence rows — `undefined` while the first load is in flight. */
  sequences: WorkItemSequenceLike[] | undefined;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useWorkItemSequences(): UseWorkItemSequencesResult {
  const [sequences, setSequences] = useState<WorkItemSequenceLike[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSequences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        setSequences([]);
        return;
      }

      const response = await fetch('/api/v1/admin/work-item-sequences', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch work item sequences');
      }

      const data = await response.json();
      setSequences(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch work item sequences');
      setSequences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSequences();
  }, [fetchSequences]);

  return { sequences, loading, error, refresh: fetchSequences };
}

export default useWorkItemSequences;
